import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MEITRE_GA_ID } from '@core/config/constants';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from './getEnvironment';
import { loadGA4 } from './scriptLoaders';
import type { AnalyticsContextValue, AnalyticsEvent, PageViewEvent } from './types';
import { AnalyticsContext } from './analyticsContext';

interface GroupAnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Group-scoped analytics provider. Same context shape as AnalyticsProvider so
 * RouteTracker and useAnalytics work unchanged, but seeded from the global
 * Meitre GA4 — no restaurant/tenant.
 *
 * Per-group GTM is loaded in GroupLandingPage (after group data arrives),
 * not here, because this provider mounts above the router before any group is
 * fetched — gtmKey would always be undefined at this point.
 *
 * trackConversion is a no-op: group landing pages have no booking conversion.
 */
export function GroupAnalyticsProvider({ children }: GroupAnalyticsProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const envRef = useRef(getAnalyticsEnvironment());

  useEffect(() => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.PRODUCTION) {
      // Global Meitre GA4 (no auto page_view — RouteTracker handles it)
      loadGA4(MEITRE_GA_ID, false);
    }

    setIsInitialized(true);
  }, []);

  // Analytics calls into window.gtag are defensively wrapped:
  // third-party shims (content blockers, defective GTM containers, Sentry
  // overrides) can throw synchronously from gtag. Without this wrapper, a
  // call inside a try/catch would abort the catch and suppress any
  // user-visible error message. Analytics is observational — its failure
  // must never break the UI.
  const safeCall = (fn: () => void, context: string): void => {
    try {
      fn();
    } catch (err) {
      // Don't escalate to Sentry from inside an analytics helper.
      // Console.warn is sufficient for DEV diagnosis; in PROD this is a no-op.
      console.warn(`[Analytics] ${context} threw:`, err);
    }
  };

  const trackPageView = useCallback((pageView: PageViewEvent) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] PageView:', pageView.path, pageView.title ?? '');
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', 'page_view', {
          page_path: pageView.path,
          page_title: pageView.title,
          send_to: MEITRE_GA_ID,
          client: 'v2-group',
        });
      }, 'trackPageView');
    }
  }, []);

  const trackEvent = useCallback((event: AnalyticsEvent) => {
    const env = envRef.current;

    if (env === ANALYTICS_ENV.LOCAL) {
      console.log('[Analytics] Event:', event.name, event.properties ?? {});
      return;
    }

    if (env === ANALYTICS_ENV.PRODUCTION && window.gtag) {
      safeCall(() => {
        window.gtag!('event', event.name, {
          ...event.properties,
          send_to: MEITRE_GA_ID,
          client: 'v2-group',
        });
      }, `trackEvent(${event.name})`);
    }
  }, []);

  const value: AnalyticsContextValue = useMemo(() => ({
    trackEvent,
    trackPageView,
    // Group landing pages have no booking conversion — no-op satisfies the
    // shared AnalyticsContextValue contract without emitting any events.
    trackConversion: () => {},
    isInitialized,
  }), [trackEvent, trackPageView, isInitialized]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
